<template>
  <b-container fluid>
    <!-- Telemetry Card -->
    <!-- <b-row class="mb-4">
      <b-col>
        <b-card>
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h6 class="mb-0"><translate>Telemetry</translate></h6>
            <div class="text-right">
              <h5 class="mb-0 text-primary">{{ calculateAICost().formatted }} USD</h5>
              <small class="text-muted">
                Input: {{ calculateAICost().breakdown.inputCost }} | Output:
                {{ calculateAICost().breakdown.outputCost }}
              </small>
            </div>
          </div>
          <pre class="mb-0" v-html="formatJson(telemetry.usageMetadata)"></pre>
        </b-card>
      </b-col>
    </b-row>
    {{ toPatch }} -->
    <!-- Control Forms -->
    <b-row class="mb-4">
      <b-col>
        <b-card :header="$gettext('Addresses Batch Processing')" header-tag="h5">
          <p class="card-text"><translate>Process multiple addresses from shipping data</translate></p>

          <b-form-group :label="$gettext('Select Date')" label-for="datepicker">
            <b-form-datepicker
              id="datepicker"
              v-model="selectedDate"
              class="mb-2"
              :placeholder="$gettext('Choose a date')"
            ></b-form-datepicker>
          </b-form-group>

          <b-form-group v-if="allowPinit">
            <b-form-checkbox switch v-model="aiConfig.pinit.enabled" id="pinit">
              <translate>Enable Pinit</translate>
            </b-form-checkbox>
          </b-form-group>

          <b-form-group>
            <b-form-checkbox switch v-model="withPickup" id="withPickup">
              <translate>With Pickup</translate>
            </b-form-checkbox>
          </b-form-group>

          <b-button
            @click="handleBatchProcessing"
            variant="success"
            size="lg"
            :disabled="processingStep !== null"
            block
          >
            <b-spinner v-if="processingStep === 'batch'" small></b-spinner>
            {{ processingStep === 'batch' ? $gettext('Processing Batch...') : $gettext('Start Batch Processing') }}
          </b-button>

          <!-- Processing Steps Indicator -->
          <div v-if="processingStep === 'batch'" class="mt-3">
            <b-progress :value="batchProgress" :max="100" show-progress animated></b-progress>
            <small class="text-muted mt-1 d-block">{{ batchProgressText }}</small>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <!-- Statistics Cards -->
    <b-row class="mb-4">
      <b-col cols="12">
        <h2 v-translate="{ total: addresses.length }">Total Addresses to Review: %{total}</h2>
      </b-col>
      <b-col v-for="stat in statistics" :key="stat.key">
        <b-card>
          <h3 :class="stat.colorClass">{{ stat.count }}</h3>
          {{ stat.label }}
        </b-card>
      </b-col>
    </b-row>

    <!-- Addresses Table -->
    <b-table :busy="!done" :items="addresses" :fields="fields" responsive>
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner class="align-middle"></b-spinner>
          <strong><translate>Working...</translate></strong>
        </div>
      </template>

      <template #cell(instructions)="row">
        {{ row.item[row.item.type + '_meta'] }}
      </template>

      <template #cell(aiAddress.gplaces)="row">
        <span v-if="row.item.aiAddress?.place?.raw">
          ✨
          <strong>
            {{
              JSON.parse(row.item.aiAddress.place.raw).google?.results[0].formatted_address ||
              JSON.parse(row.item.aiAddress.place.raw).results[0].formatted_address
            }}
          </strong>
        </span>
        <!-- <strong>{{ row.item.aiAddress.gplaces }}</strong>
        <br />
        <small v-if="row.item.aiAddress?.place?.raw">
          Raw:
          <strong>{{
            JSON.parse(row.item.aiAddress.place.raw).google?.results[0].formatted_address ||
            JSON.parse(row.item.aiAddress.place.raw).results[0].formatted_address
          }}</strong>
        </small> -->
      </template>

      <template #cell(aiAddress.confidence)="row">
        <b-badge :variant="getConfidenceBadgeVariant(row.item.aiAddress.confidence)">
          {{ row.item.aiAddress.confidence }}% {{ row.item.aiAddress.engine }}
        </b-badge>
      </template>

      <template #cell(actions)="row">
        <div class="text-nowrap">
          <b-button
            size="sm"
            @click="aiAccepted(row.item)"
            :variant="
              (toPatch.findIndex(x => x.id === row.item.id) !== -1 &&
              toPatch.find(x => x.id === row.item.id).meta.ai.addresses[row.item.type].accepted === true
                ? ''
                : 'outline-') + 'success'
            "
            :disabled="!row.item.aiAddress.place?.id"
          >
            <font-awesome-icon icon="thumbs-up" />
          </b-button>
          <b-button
            size="sm"
            @click="aiRejected(row.item)"
            :variant="
              (toPatch.findIndex(x => x.id === row.item.id) !== -1 &&
              toPatch.find(x => x.id === row.item.id).meta.ai.addresses[row.item.type].accepted === false
                ? ''
                : 'outline-') + 'danger'
            "
            :disabled="!row.item.aiAddress.place?.id"
          >
            <font-awesome-icon icon="thumbs-down" />
          </b-button>
          <b-button
            size="sm"
            @click.stop="
              openMapOnCoords(
                row.item.aiAddress.place.lat,
                row.item.aiAddress.place.lng,
                19,
                JSON.parse(row.item.aiAddress.place.raw).google?.results[0].formatted_address ||
                  JSON.parse(row.item.aiAddress.place.raw).results[0].formatted_address.replaceAll(' ', '+')
              )
            "
            :title="$gettext('Quick view address map on new window')"
            :disabled="!row.item.aiAddress.place?.id"
          >
            <font-awesome-icon :icon="['fa', 'map-marked-alt']" />
          </b-button>
        </div>
      </template>
    </b-table>
    <div
      class="w-100 py-4 text-center bg-white"
      style="
        position: fixed;
        bottom: 0;
        left: 0;
        border-top: 1px solid #dee2e6;
        box-shadow: 0 0 15px rgba(0, 0, 0, 0.1);
      "
      v-if="toPatch.length"
    >
      <b-button
        @click="patchShippings"
        variant="primary"
        v-translate="{ count: toPatch.length }"
        :translate-n="toPatch.length"
        translate-plural="Update %{count} Shippings"
      >
        Update %{count} Shipping
      </b-button>
      <br />
      <b-form-checkbox v-model="reprocess" switch class="mt-3"> Reprocess after update </b-form-checkbox>
    </div>
  </b-container>
</template>

<script>
/**
 * AI Bulk Address Reviewer Component
 *
 * This component provides AI-powered address cleaning, validation, and standardization
 * for batch processing of addresses from shipping data.
 */

import AddressMixins from '@/mixins/address.mixin.js'
import AddressProcessingMixin from '@/mixins/address-processing.mixin.js'
import AiService from '@/services/ai-service'
import AiTelemetryMixin from '@/mixins/ai-telemetry.mixin'
import AiUtilsMixin from '@/mixins/ai-utils.mixin'

export default {
  mixins: [AddressMixins, AddressProcessingMixin, AiTelemetryMixin, AiUtilsMixin],

  data() {
    return {
      aiConfig: {
        aiVersion: 'v2.0',
        MODELS: {
          CLEAN: 'gemini-2.5-flash',
          CONFIDENCE: 'gemini-2.5-flash-lite',
          FINAL_CONFIDENCE: 'gemini-2.5-flash-lite'
        },
        batch: {
          size: 10,
          delay: 1000
        },
        thresholds: {
          pinitActivation: 75,
          pinitCoordinates: 0.85,
          pinitLastResource: 79
        },
        pinit: {
          enabled: false,
          validation: true,
          geocoding: true
        }
      },
      addresses: [],
      telemetry: null,
      fields: [
        { key: 'instructions', label: 'Instructions' },
        { key: 'aiAddress.original', label: 'Original' },
        { key: 'aiAddress.clean', label: 'Clean' },
        { key: 'aiAddress.gplaces', label: 'Google Places' },
        { key: 'aiAddress.confidence', label: 'Confidence' },
        { key: 'actions', label: 'Actions' }
      ],
      done: true,
      aiService: null,
      batchProgress: 0,
      batchProgressText: '',
      aiAddress: {},
      selectedDate: new Date().toISOString().slice(0, 10),
      toPatch: [],
      allowPinit: false,
      reprocess: false,
      withPickup: false
    }
  },

  computed: {
    statistics() {
      if (!this.addresses.length) return []

      const errorCount = this.addresses.filter(x => x.aiAddress.clean === 'ERROR').length
      const gplacesErrorCount = this.addresses.filter(x => x.aiAddress.gplaces === 'FAIL').length
      const acceptableCount = this.addresses.filter(x => x.aiAddress.confidence >= 50).length

      return [
        {
          key: 'errors',
          count: errorCount,
          label: this.$gettext('Catastrofic cleaning errors (no street and number found)'),
          colorClass: 'text-danger'
        },
        {
          key: 'gplaces_errors',
          count: gplacesErrorCount,
          label: this.$gettext('Gplaces errors'),
          colorClass: 'text-danger'
        },
        {
          key: 'acceptable',
          count: acceptableCount,
          label: this.$gettext('Potentially acceptable changes (>=50%)'),
          colorClass: 'text-success'
        }
      ]
    }
    // We need 'processingStep' which is used in the template and mixin.
    // In address-processing.mixin.js, 'processingStep' is defined in data().
    // So distinct is already handled by mixin merge strategy (component data overwrites mixin data if keys conflict,
    // but here we didn't define it in component data, so it uses mixin's 'processingStep').
    // Wait, in ai_address_reviewer.vue it WAS defined in data but I removed it from the copied data().
    // Let's check the mixin content again.
    // AddressProcessingMixin has `processingStep` in data. Good.
  },

  async mounted() {
    this.aiConfig = this.deepMerge(this.aiConfig, this.$frontConfig.ai?.checkAddress?.config || {})
    if (this.aiConfig.pinit.enabled) {
      this.allowPinit = true
    }
    this.aiConfig.pinit.enabled = false
    this.aiService = new AiService()
    this.telemetry = this.createInitialTelemetry(this.aiConfig.MODELS)
  },

  methods: {
    /**
     * Main workflow for processing all addresses
     * Executes: fetch → clean → autocomplete → calculate confidence
     */
    async processAddresses() {
      try {
        this.done = false
        await this.fetchShippings()
        await this.cleanAddresses()
        await this.delay(this.aiConfig.batch.delay)
        await this.autocompleteAddresses()
        await this.delay(this.aiConfig.batch.delay)
        await this.setConfidences()
      } catch (error) {
        console.error('Error processing addresses:', error)
      } finally {
        this.done = true
      }
    },

    /**
     * Fetches shipping records from API for batch processing
     * Filters for non-outsourced shipments with field revision
     */
    async fetchShippings() {
      const params = {
        sort: [{ key: 'updated_at', direction: 'asc' }],
        limit: 500,
        page: 0,
        filter_groups: [
          {
            filters: [
              // ['id', 'eq', '6af780c0-d5f2-11f0-adac-350da9287b10'],
              // ['id', 'eq', '75049610-d5f5-11f0-8d6b-3d336c3dfd61'],
              ['is_outsourced', 'eq', 'false'],
              ['status', 'eq', 'DRAFT'],
              ['fields_revision', 'eq', '1'],
              [
                'updated_at',
                'bt',
                [
                  this.moment.tz(this.selectedDate, this.$tz).startOf('day').utc().format('YYYY-MM-DD HH:mm:ss'),
                  this.moment.tz(this.selectedDate, this.$tz).endOf('day').utc().format('YYYY-MM-DD HH:mm:ss')
                ]
              ],
              ['from_country_iso_code', 'eq', window.country]
            ]
          }
        ]
      }

      if (this.withPickup) {
        params.filter_groups[0].filters.push(['pickup_shipping_id', 'eq', null, true])
      }

      const response = await this.$http.get(`${this.$appConfig.api.baseUrl}shippings`, { params })
      this.addresses = []

      let type = 'to'
      let checkpointFields = this.setCheckpointFields(type)
      this.addresses = response.body.data
        .map(x => {
          return {
            id: x.id,
            [checkpointFields.metaParam]: x[checkpointFields.metaParam],
            type,
            aiAddress: {
              original: this.originalString(x, checkpointFields.originalParam),
              aiVersion: this.aiConfig.aiVersion
            }
          }
        })
        .filter(x => x.aiAddress.original)
        .filter(x => !!x.aiAddress.original?.match(/\d/))
        .filter(x => !x.aiAddress.original?.toLowerCase().includes('sn'))
        .filter(x => !x.aiAddress.original?.toLowerCase().includes('s/n'))

      type = 'from'
      checkpointFields = this.setCheckpointFields(type)
      this.addresses = this.addresses.concat(
        ...response.body.data
          .map(x => {
            return {
              id: x.id,
              [checkpointFields.metaParam]: x[checkpointFields.metaParam],
              type,
              aiAddress: {
                original: this.originalString(x, checkpointFields.originalParam),
                aiVersion: this.aiConfig.aiVersion
              }
            }
          })
          .filter(x => x.aiAddress.original)
          .filter(x => !!x.aiAddress.original?.match(/\d/))
          .filter(x => !x.aiAddress.original?.toLowerCase().includes('sn'))
          .filter(x => !x.aiAddress.original?.toLowerCase().includes('s/n'))
      )
    },

    /**
     * Cleans all addresses in batches using AI
     * Processes in parallel within each batch
     */
    async cleanAddresses() {
      const uniqueOriginals = [...new Set(this.addresses.map(x => x.aiAddress.original))]
      const uniqueItems = uniqueOriginals.map(original => ({ original }))
      const batches = this.createBatches(uniqueItems, this.aiConfig.batch.size)

      for (const batch of batches) {
        const promises = batch.map(item => this.cleanAddress(item.original))
        await Promise.all(promises)
        await this.delay(this.aiConfig.batch.delay)
        this.aiService = new AiService()
      }
    },

    /**
     * Cleans a single address using AI
     * @param {string} address - Raw address string
     * @param {number} index - Address index in array
     */
    async cleanAddress(address) {
      console.log('Cleaning address:', address, 'Type:', typeof address)
      if (!address || typeof address !== 'string') {
        console.error('Invalid address passed to cleanAddress')
        return
      }

      const items = this.addresses.filter(x => x.aiAddress.original === address)

      try {
        console.log('Sending message to AI Service...')
        const response = await this.aiService.sendMessage(this.aiConfig.MODELS.CLEAN, this.PROMPTS.CLEAN, address, 0)

        this.updateTelemetry(this.aiConfig.MODELS.CLEAN, response.usageMetadata)

        const parsed = this.parseToonResponse(response.text)

        if (parsed) {
          if (!parsed.data && parsed.address) {
            this.cleanAddress(address)
          } else if ((parsed.error && parsed.error.length !== 0) || parsed.data.length === 0) {
            items.forEach(item => {
              this.$set(item.aiAddress, 'clean', 'ERROR')
              this.$set(item.aiAddress, 'cleanOptions', [])
            })
          } else {
            const sortedOptions = parsed.data.sort((a, b) => b.confidence - a.confidence)
            items.forEach(item => {
              this.$set(item.aiAddress, 'clean', sortedOptions[0].guess)
              this.$set(item.aiAddress, 'cleanOptions', sortedOptions)
            })
          }
        } else {
          items.forEach(item => {
            this.$set(item.aiAddress, 'clean', 'ERROR')
            this.$set(item.aiAddress, 'cleanOptions', [])
          })
        }
      } catch (error) {
        console.error('Error cleaning address:', error)
        items.forEach(item => {
          this.$set(item.aiAddress, 'clean', 'ERROR')
          this.$set(item.aiAddress, 'cleanOptions', [])
        })
      }
    },

    /**
     * Processes autocomplete for all addresses in parallel
     */
    async autocompleteAddresses() {
      const uniqueOriginals = [...new Set(this.addresses.map(x => x.aiAddress.original))]
      const uniqueItems = uniqueOriginals.map(original => ({ original }))
      const batches = this.createBatches(uniqueItems, this.aiConfig.batch.size)

      for (const batch of batches) {
        const promises = batch.map(item => this.autocompleteAddress(item.original))
        await Promise.all(promises)
        await this.delay(this.aiConfig.batch.delay)
      }
    },

    /**
     * Performs Google Places autocomplete for a single address
     * Tries all clean options and optionally uses Pinit for improvement
     * @param {string} address - Cleaned address string
     * @param {number} index - Address index in array
     */
    async autocompleteAddress(original) {
      const items = this.addresses.filter(x => x.aiAddress.original === original)
      const address = items[0].aiAddress.clean

      if (address === 'ERROR') {
        items.forEach(item => this.markAddressAsFailed(item.aiAddress))
        return
      }

      try {
        const cleanOptions = items[0].aiAddress.cleanOptions || [{ guess: address, confidence: 100 }]
        const allAutocompleteResults = []

        for (const cleanOption of cleanOptions) {
          const encodedAddress = this.encodeAddressForAPI(cleanOption.guess)
          const response = await this.$http.get(`${this.$appConfig.api.baseUrl}autocomplete?query=${encodedAddress}`)
          allAutocompleteResults.push(...response.body)
          await this.delay(this.aiConfig.batch.delay)
        }

        if (allAutocompleteResults.length === 0) {
          if (this.usePinit) {
            const result = await this.tryPinitImprovement(original, cleanOptions[0].guess, 'batch')

            if (result.success) {
              const promises = items.map(item => this.applyPinitResult(item.aiAddress, result, 'batch'))
              await Promise.all(promises)
              return
            }
          }

          items.forEach(item => this.markAddressAsFailed(item.aiAddress))
          return
        }

        const results = allAutocompleteResults.map(
          x => x.main_text + (x.secondary_text !== '' ? ', ' + x.secondary_text : '')
        )

        items.forEach(item => {
          this.$set(item.aiAddress, 'autocomplete', { res: results })
          this.$set(item.aiAddress, 'orderedAutocomplete', { res: results })
        })

        if (allAutocompleteResults.length === 1) {
          await this.setSinglePlaceResult(allAutocompleteResults, items)
        } else {
          const bestCleanAddress = cleanOptions[0].guess
          await this.setMultiplePlaceResults(bestCleanAddress, allAutocompleteResults, items)
        }
      } catch (error) {
        console.error('Error in autocomplete:', error)
        items.forEach(item => this.markAddressAsFailed(item.aiAddress))
      }
    },

    /**
     * Handles single autocomplete result validation
     * @param {Array} places - Array with single place result
     * @param {number} index - Address index in array
     */
    async setSinglePlaceResult(places, items) {
      console.log('Setting single place result:', places, items)

      const placeResult = await this.checkPlaces(places)

      if (!placeResult || !placeResult.formatted_address || !placeResult.is_valid) {
        if (this.usePinit) {
          const cleanAddress = items[0].aiAddress.clean
          const result = await this.tryPinitImprovement(items[0].aiAddress.original, cleanAddress, 'batch')

          if (result.success) {
            const promises = items.map(item => this.applyPinitResult(item.aiAddress, result, 'batch'))
            await Promise.all(promises)
            return
          }
        }

        items.forEach(item => {
          this.$set(item.aiAddress, 'place', null)
          this.$set(item.aiAddress, 'gplaces', 'FAIL')
        })
        return
      }

      items.forEach(item => {
        this.$set(item.aiAddress, 'place', placeResult)
        this.$set(item.aiAddress, 'gplaces', placeResult.formatted_address)
      })
    },

    /**
     * Handles multiple autocomplete results by ranking them by confidence
     * @param {string} cleanAddress - Cleaned address for comparison
     * @param {Array} places - Array of place results
     * @param {number} index - Address index in array
     */
    async setMultiplePlaceResults(cleanAddress, places, items) {
      console.log('Setting multiple place results:', cleanAddress, places, items)

      let confidences = await this.setAcConfidences(cleanAddress, places)
      confidences = this.normalizeConfidences(confidences)

      // Deduplicate based on place_id
      const seen = new Set()
      confidences = confidences.filter(el => {
        const duplicate = seen.has(el.place_id)
        seen.add(el.place_id)
        return !duplicate
      })

      console.log('Confidences:', confidences.length, confidences)

      const sortedConfidences = confidences
        ?.sort((a, b) => parseInt(b.confidence) - parseInt(a.confidence))
        .filter(x => x.confidence > 25)
        .filter(x => {
          const mainTextParts = (x.main_text + ', ' + x.secondary_text).split(', ')
          return !!(mainTextParts[0].match(/\d/gi) || mainTextParts[1].match(/\d/gi))
        })
        .filter(x => !!x.place_id)

      console.log('Sorted confidences:', sortedConfidences.length, sortedConfidences)

      if (this.usePinit) {
        const result = await this.tryPinitImprovement(items[0].aiAddress.original, cleanAddress, 'batch')

        if (result.success) {
          const promises = items.map(item => this.applyPinitResult(item.aiAddress, result, 'batch'))
          await Promise.all(promises)
          return
        }
      }

      items.forEach(item => {
        this.$set(item.aiAddress, 'orderedAutocomplete', {
          res: sortedConfidences.map(
            x => `${x.main_text}${x.secondary_text !== '' ? ', ' + x.secondary_text : ''} (${x.confidence}%)`
          )
        })
      })

      const placeResult = await this.checkPlaces(sortedConfidences)
      items.forEach(item => {
        this.$set(item.aiAddress, 'place', placeResult)
        this.$set(item.aiAddress, 'gplaces', placeResult ? placeResult.formatted_address : 'FAIL')
      })
    },

    /**
     * Calculates final confidence scores for all addresses
     * Uses Pinit score when available, otherwise calculates with AI
     */
    async setConfidences() {
      const uniqueOriginals = [...new Set(this.addresses.map(x => x.aiAddress.original))]
      const uniqueItems = uniqueOriginals.map(original => ({ original }))
      const batches = this.createBatches(uniqueItems, this.aiConfig.batch.size)

      for (const batch of batches) {
        const promises = batch.map(async item => {
          const original = item.original
          const items = this.addresses.filter(x => x.aiAddress.original === original)
          const address = items[0].aiAddress

          if (address.pinitImproved && address.pinitResult?.geocoding?.score) {
            const pinitConfidence = Math.round(address.pinitResult.geocoding.score * 100)

            // If we already have both scores, use the minimum
            if (address.geminiConfidence !== undefined && address.pinitConfidence !== undefined) {
              const finalConfidence = Math.min(address.geminiConfidence, address.pinitConfidence)
              items.forEach(i => {
                this.$set(i.aiAddress, 'confidence', finalConfidence)
                this.$set(i.aiAddress, 'engine', 'Pinit')
              })
              console.log(
                `Using lower confidence score for Pinit (batch): ${finalConfidence} (Gemini: ${address.geminiConfidence}, Pinit: ${address.pinitConfidence})`
              )
              return
            }

            // If we don't have Gemini confidence, calculate it first
            if (address.clean !== 'ERROR' && address.gplaces !== 'FAIL') {
              try {
                const addressString = `${address.original}|${address.clean}|${address.gplaces}`
                const response = await this.aiService.sendMessage(
                  this.aiConfig.MODELS.FINAL_CONFIDENCE,
                  this.PROMPTS.CONFIDENCE,
                  addressString,
                  0
                )
                const geminiConfidence = this.extractConfidenceScore(response.text)

                // Now use MIN of both scores
                const finalConfidence = Math.min(geminiConfidence, pinitConfidence)
                items.forEach(i => {
                  this.$set(i.aiAddress, 'confidence', finalConfidence)
                  this.$set(i.aiAddress, 'geminiConfidence', geminiConfidence)
                  this.$set(i.aiAddress, 'pinitConfidence', pinitConfidence)
                  this.$set(i.aiAddress, 'engine', 'Pinit')
                })
                console.log(
                  `Using lower confidence score for Pinit (batch): ${finalConfidence} (Gemini: ${geminiConfidence}, Pinit: ${pinitConfidence})`
                )
                return
              } catch (error) {
                console.error('Error calculating Gemini confidence for Pinit result:', error)
                // Fallback to Pinit score only if Gemini fails
                items.forEach(i => {
                  this.$set(i.aiAddress, 'confidence', pinitConfidence)
                  this.$set(i.aiAddress, 'engine', 'Pinit')
                })
                return
              }
            } else {
              // If clean or gplaces failed, use Pinit score only
              items.forEach(i => {
                this.$set(i.aiAddress, 'confidence', pinitConfidence)
                this.$set(i.aiAddress, 'engine', 'Pinit')
              })
              return
            }
          }

          if (address.clean !== 'ERROR' && address.gplaces !== 'FAIL') {
            return this.setConfidence(`${address.original}|${address.clean}|${address.gplaces}`, items)
          } else {
            items.forEach(i => {
              this.$set(i.aiAddress, 'confidence', 0)
              this.$set(i.aiAddress, 'engine', 'Error')
            })
            return Promise.resolve()
          }
        })
        await Promise.all(promises)
        await this.delay(this.aiConfig.batch.delay)
      }
      this.aiService = new AiService()
    },

    /**
     * Calculates confidence score for a single address
     * @param {string} addressString - Combined clean and gplaces address
     * @param {number} index - Address index in array
     */
    async setConfidence(addressString, items) {
      try {
        const response = await this.aiService.sendMessage(
          this.aiConfig.MODELS.FINAL_CONFIDENCE,
          this.PROMPTS.CONFIDENCE,
          addressString,
          0
        )

        this.updateTelemetry(this.aiConfig.MODELS.FINAL_CONFIDENCE, response.usageMetadata)

        const parsed = this.parseToonResponse(response.text)
        const confidenceScore = parsed?.confidence || 0
        items.forEach(i => {
          this.$set(i.aiAddress, 'confidence', confidenceScore)
          this.$set(i.aiAddress, 'engine', 'Gemini')
        })

        // If confidence is 70 or less and Pinit is available, try improvement
        if (confidenceScore <= this.aiConfig.thresholds.pinitLastResource && this.usePinit) {
          const addressObj = items[0].aiAddress
          const bestCleanAddress = addressObj.cleanOptions?.[0]?.guess || addressObj.clean
          console.log(`Low confidence score (${confidenceScore}) for batch address, trying Pinit improvement...`)

          const result = await this.tryPinitImprovement(addressObj.original, bestCleanAddress, 'batch')

          if (result.success) {
            const promises = items.map(i => this.applyPinitResult(i.aiAddress, result, 'batch'))
            await Promise.all(promises)
            console.log('Batch address improved with Pinit after low confidence score')
          }
        }
      } catch (error) {
        console.error('Error setting confidence:', error)
        items.forEach(i => {
          this.$set(i.aiAddress, 'confidence', 0)
          this.$set(i.aiAddress, 'engine', 'Error')
        })
      }
    },

    /**
     * Handles batch processing of multiple addresses
     * Fetches shipping data and processes all addresses
     */
    async handleBatchProcessing() {
      this.processingStep = 'batch'
      this.batchProgress = 0
      this.batchProgressText = this.$gettext('Starting batch processing...')
      this.done = false
      this.addresses = []

      try {
        // Fetch shippings
        this.batchProgressText = this.$gettext('Fetching shipping data...')
        this.batchProgress = 10
        await this.fetchShippings()

        // Clean addresses
        this.batchProgressText = this.$gettext('Cleaning addresses...')
        this.batchProgress = 30
        await this.cleanAddresses()

        // Delay
        this.batchProgressText = this.$gettext('Processing autocomplete...')
        this.batchProgress = 50
        await this.delay(this.aiConfig.batch.delay)

        // Autocomplete addresses
        await this.autocompleteAddresses()

        // Delay
        this.batchProgressText = this.$gettext('Calculating confidence scores...')
        this.batchProgress = 80
        await this.delay(this.aiConfig.batch.delay)

        // Set confidences
        await this.setConfidences()

        this.batchProgress = 100
        this.batchProgressText = this.$gettext('Batch processing completed!')

        this.makeToast(
          this.$gettext('Batch Processing Complete'),
          this.$gettextInterpolate(this.$gettext('Successfully processed %{ count } addresses'), {
            count: this.addresses.length
          }),
          'success',
          false,
          5000
        )
      } catch (error) {
        console.error('Error in batch processing:', error)

        this.makeToast(
          this.$gettext('Batch Processing Error'),
          error.message || this.$gettext('Batch processing failed'),
          'danger',
          false,
          5000
        )
      } finally {
        this.processingStep = null
        this.done = true
      }
    },

    /**
     * Helper: Renders address list for table cells
     * @param {Object} value - Address result value
     * @returns {Array} Array of address strings
     */
    formatAddressResultList(value) {
      return value?.res || []
    },

    /**
     * Clears all address data and resets telemetry
     */
    clearAllData() {
      this.addresses = []
      this.telemetry = this.createInitialTelemetry(this.aiConfig.MODELS)
      this.done = false
    },
    originalString(shipping, originalParam) {
      if (shipping.fields_revision_data && shipping.fields_revision_data[originalParam]) {
        if (shipping.fields_revision_data[originalParam].address) {
          return shipping.fields_revision_data[originalParam].address
        } else {
          let str = ''
          str += shipping.fields_revision_data[originalParam].street
            ? shipping.fields_revision_data[originalParam].street + ' '
            : ''
          str += shipping.fields_revision_data[originalParam].number
            ? shipping.fields_revision_data[originalParam].number + ','
            : ''
          str += shipping.fields_revision_data[originalParam].city
            ? shipping.fields_revision_data[originalParam].city + ','
            : ''
          str += shipping.fields_revision_data[originalParam].postalCode
            ? shipping.fields_revision_data[originalParam].postalCode + ','
            : ''
          str += shipping.fields_revision_data[originalParam].state
            ? shipping.fields_revision_data[originalParam].state + ','
            : ''
          str += window.countries.find(x => x.iso_code === shipping.to_country_iso_code).name
          return str
        }
      }
    },
    setCheckpointFields(type) {
      if (type === 'to') {
        return {
          placeIdParam: 'to_place_id',
          metaParam: 'to_meta',
          extraParam: 'to_extra',
          originalParam: 'to_original'
        }
      } else {
        return {
          placeIdParam: 'from_place_id',
          metaParam: 'from_meta',
          extraParam: 'from_extra',
          originalParam: 'from_original'
        }
      }
    },
    aiAccepted(originalShipping) {
      const checkpointFields = this.setCheckpointFields(originalShipping.type)
      let toPatch = this.addresses.filter(x => x.aiAddress?.place?.id === originalShipping.aiAddress.place.id)

      for (let originalShipping of toPatch) {
        let shipping = { ...originalShipping }
        if (this.toPatch.findIndex(x => x.id === shipping.id) === -1) {
          shipping.meta = {
            ...shipping.meta,
            ai: {
              addresses: {
                [shipping.type]: {
                  accepted: true,
                  aiAddress: shipping.aiAddress
                }
              }
            }
          }
          shipping[checkpointFields.metaParam] =
            (shipping[checkpointFields.metaParam] || '') + '\nOriginal: ' + shipping.aiAddress.original
          shipping[shipping.type + '_place_id'] = shipping.aiAddress.place.id
          delete shipping.aiAddress
          this.toPatch.push(shipping)
        } else {
          this.toPatch.splice(
            this.toPatch.findIndex(x => x.id === shipping.id),
            1
          )
        }
      }
    },
    aiRejected(originalShipping) {
      let toPatch = this.addresses.filter(x => x.aiAddress?.place?.id === originalShipping.aiAddress.place.id)

      for (let originalShipping of toPatch) {
        let shipping = { ...originalShipping }
        if (this.toPatch.findIndex(x => x.id === shipping.id) === -1) {
          shipping.meta = {
            ...shipping.meta,
            ai: {
              addresses: {
                [shipping.type]: {
                  accepted: false,
                  aiAddress: shipping.aiAddress
                }
              }
            }
          }
          shipping[shipping.type + '_place_id'] = null
          delete shipping.aiAddress
          this.toPatch.push(shipping)
        } else {
          this.toPatch.splice(
            this.toPatch.findIndex(x => x.id === shipping.id),
            1
          )
        }
      }
    },
    openMapOnCoords(lat, lng, z, place = null) {
      if (!place || window.country === 'CO') {
        window.open(`https://www.google.com/maps/place/@${lat},${lng},${z}z`)
      } else {
        window.open(`https://www.google.com/maps/place/${place}/@${lat},${lng},${z}z`)
      }
    },
    async patchShippings() {
      for (const shipping of this.toPatch) {
        await this.$http.patch(this.$appConfig.api.baseUrl + 'shippings/' + shipping.id, shipping)
        this.addresses.splice(
          this.addresses.findIndex(x => x.id === shipping.id),
          1
        )
      }
      this.toPatch = []
      if (this.reprocess) {
        this.handleBatchProcessing()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.mb-0 {
  margin-bottom: 0 !important;
}

.small {
  font-size: 0.875rem;
}

.card-text {
  color: #6c757d;
}

pre {
  font-size: 0.8rem;
  max-height: 200px;
  overflow-y: auto;
}
</style>
